/**
 * ProductSwitch Custom Element
 *
 * Handles async product switching when clicking external color swatch links.
 * Intercepts navigation and uses Shopify's Section Rendering API to update
 * page content without a full reload.
 */
if (!customElements.get('product-switch')) {
  customElements.define(
    'product-switch',
    class ProductSwitch extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.productUrl = this.dataset.productUrl;
        this.boundPopstateHandler = this.onPopstate.bind(this);
      }

      connectedCallback() {
        this.attachEventListeners();
        window.addEventListener('popstate', this.boundPopstateHandler);
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.boundPopstateHandler);
      }

      attachEventListeners() {
        // Listen for clicks on external product swatch links
        this.querySelectorAll('.product-form__swatch-link').forEach((link) => {
          link.addEventListener('click', this.onExternalProductClick.bind(this));
        });
      }

      /**
       * Main click handler for external product swatch links
       * @param {Event} event - Click event
       */
      async onExternalProductClick(event) {
        event.preventDefault();

        const link = event.currentTarget;
        const targetUrl = link.getAttribute('href');

        if (!targetUrl) {
          return;
        }

        // Get the swatch wrapper for loading state
        const swatchWrapper = link.closest('.product-form__input-wrapper');

        try {
          this.showLoadingState(swatchWrapper);

          // Store scroll position before replacement
          const scrollY = window.scrollY;

          // Fetch the full product page (not just section) to ensure all content is present
          const html = await this.fetchProductPage(targetUrl);
          await this.replaceSectionContent(html);

          // Restore scroll position
          window.scrollTo(0, scrollY);

          // Update browser URL
          history.pushState({ productSwitch: true }, '', targetUrl);

          // Reinitialize components
          this.reinitializeComponents();
        } catch (error) {
          console.error('ProductSwitch: Failed to load product', error);
          // Fallback to regular navigation
          window.location.href = targetUrl;
        }
      }

      /**
       * Fetch the full product page HTML
       * @param {string} targetUrl - Target product URL
       * @returns {Promise<string>} HTML content
       */
      async fetchProductPage(targetUrl) {
        const response = await fetch(targetUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.text();
      }

      /**
       * Replace the current section content with new HTML
       * @param {string} html - Full page HTML content
       */
      async replaceSectionContent(html) {
        // Parse the response HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Find the main product section in response
        const newSection = doc.querySelector('section[id^="MainProduct-"]');

        if (!newSection) {
          throw new Error('Could not find MainProduct section in response');
        }

        // Find the current section on the page
        const currentSection = document.querySelector('section[id^="MainProduct-"]');

        if (!currentSection) {
          throw new Error('Could not find MainProduct section on page');
        }

        // Cancel scroll animations on new content before inserting
        // This prevents elements from being invisible due to animation initial state
        this.cancelScrollAnimations(newSection);

        // Replace the innerHTML using the script re-injection pattern
        this.setInnerHTML(currentSection, newSection.innerHTML);

        // Update section attributes if they changed
        Array.from(newSection.attributes).forEach((attr) => {
          currentSection.setAttribute(attr.name, attr.value);
        });
      }

      /**
       * Cancel scroll-triggered animations to ensure content is visible immediately
       * Elements with scroll-trigger animate--slide-in start at opacity 0.01
       * and need IntersectionObserver to become visible. After dynamic replacement,
       * we need to force visibility by adding the cancel class.
       * @param {HTMLElement} element - Element to process
       */
      cancelScrollAnimations(element) {
        // Find all scroll-trigger elements and force them to be visible
        const scrollTriggerElements = element.querySelectorAll('.scroll-trigger');
        scrollTriggerElements.forEach((el) => {
          // Add the cancel class which sets opacity: 1 and disables animation
          el.classList.add('scroll-trigger--cancel');
        });

        // Also check if the element itself has scroll-trigger
        if (element.classList.contains('scroll-trigger')) {
          element.classList.add('scroll-trigger--cancel');
        }
      }

      /**
       * Set innerHTML and reinject scripts to allow execution
       * Pattern from quick-add.js
       * @param {HTMLElement} element - Element to update
       * @param {string} html - HTML to insert
       */
      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution
        // By default, scripts are disabled when using element.innerHTML
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      /**
       * Reinitialize Shopify components after content replacement
       */
      reinitializeComponents() {
        // Reinitialize Shopify Payment Button
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        // Reinitialize Product Model (3D viewer)
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      /**
       * Show loading state on the clicked swatch
       * @param {HTMLElement} swatchWrapper - The swatch wrapper element
       */
      showLoadingState(swatchWrapper) {
        // Add loading class to the fieldset for overall loading state
        const fieldset = this.querySelector('fieldset');
        if (fieldset) {
          fieldset.classList.add('product-switch--loading');
        }

        // Add loading class to the specific swatch
        if (swatchWrapper) {
          swatchWrapper.classList.add('product-switch__swatch--loading');
        }
      }

      /**
       * Hide loading state
       * Note: This is called after content replacement, so the old elements
       * will be gone. New content won't have loading classes.
       */
      hideLoadingState() {
        const fieldset = this.querySelector('fieldset');
        if (fieldset) {
          fieldset.classList.remove('product-switch--loading');
        }

        this.querySelectorAll('.product-switch__swatch--loading').forEach((el) => {
          el.classList.remove('product-switch__swatch--loading');
        });
      }

      /**
       * Handle browser back/forward navigation
       * Reload the page to ensure correct state
       */
      onPopstate() {
        window.location.reload();
      }
    }
  );
}
